import NeptuneElement from '../../NeptuneElement.js';

class NeptuneMap extends NeptuneElement {

    templateMarkup = `
    <div id="map" role="application" aria-label="Interactive map"></div>
    <div id="info-panel" role="dialog" aria-labelledby="site-name">
      <div id="site-name"></div>
      <div id="site-address"></div>
      <button id="redirect-btn" aria-label="Go to site">Go to site</button>
    </div>
    <div id="legend">
      <div class="legend-item">
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path d="M 8 2 L 14 8 L 8 14 L 2 8 Z" fill="#992600"/>
        </svg>
        <span>Critical</span>
      </div>
      <div class="legend-item">
        <svg width="16" height="16" viewBox="0 0 16 16">
          <rect x="2" y="2" width="12" height="12" fill="#CC3300"/>
        </svg>
        <span>High Risk</span>
      </div>
      <div class="legend-item">
        <svg width="16" height="16" viewBox="0 0 16 16">
          <path d="M 8 2 L 14 14 L 2 14 Z" fill="#C28B00"/>
        </svg>
        <span>Medium Risk</span>
      </div>
      <div class="legend-item">
        <svg width="16" height="16" viewBox="0 0 16 16">
          <circle cx="8" cy="8" r="6" fill="#00A653"/>
        </svg>
        <span>Low Risk</span>
      </div>
    </div>
    <div id="loading-overlay" aria-live="polite" aria-busy="true">
      <div class="spinner"></div>
      <span>Loading...</span>
    </div>
    <div id="error-overlay" role="alert" aria-live="assertive"></div>
  `;

    componentCssUrl = '/src/components/dataVisualization/map/map.css';

    azureKey = MICROSOFT_MAP_KEY;
    sites = [];
    mapReady = false;
    initialized = false;
    lastSignature = '';
    hasFitBounds = false;
    currentSiteId = null;
    justOpened = false;
    infoPanel = null;
    loadingOverlay = null;
    errorOverlay = null;
    map = null;
    ds = null;

    constructor() {
        super();
    }

    async connectedCallback() {
        await super.connectedCallback();

        this.infoPanel = this.shadowRoot.getElementById('info-panel');
        this.loadingOverlay = this.shadowRoot.getElementById('loading-overlay');
        this.errorOverlay = this.shadowRoot.getElementById('error-overlay');

        this.infoPanel.style.display = 'none';
        this.infoPanel.classList.remove('visible');
        this.loadingOverlay.classList.remove('visible');
        this.loadingOverlay.style.display = 'none';
        this.errorOverlay.classList.remove('visible');

        if (!this.initialized) {
            this.initialized = true;
            this.setLoading(true);

            if (!this.azureKey) {
                this.showError('Azure Maps key is required');
                this.setLoading(false);
                return;
            }

            this.initializeMap();
        }
    }

    async disconnectedCallback() {
        if (this.map) {
            this.map.dispose();
            this.map = null;
        }
        await super.disconnectedCallback();
    }

    setEventListeners() {
        const redirectBtn = this.shadowRoot.getElementById('redirect-btn');
        redirectBtn?.addEventListener('click', (e) => {
            e.stopPropagation();
            if (this.currentSiteId) {
                this.dispatchEvent(new CustomEvent('site-redirect', {
                    detail: { siteId: this.currentSiteId },
                    bubbles: true,
                    composed: true
                }));
            }
        });

        this.infoPanel?.addEventListener('click', (e) => {
            e.stopPropagation();
        });

        this.addEventListener('click', (e) => {
            if (this.justOpened) {
                return;
            }

            if (this.infoPanel.classList.contains('visible')) {
                const clickedOnPanel = this.infoPanel.contains(e.target);
                if (!clickedOnPanel) {
                    this.hideInfoPanel();
                }
            }
        });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.infoPanel.classList.contains('visible')) {
                this.hideInfoPanel();
            }
        });
    }

    removeEventListeners() {
    }

    initializeMap() {
        try {
            // eslint-disable-next-line
            this.map = new atlas.Map(this.shadowRoot.getElementById('map'), {
                authOptions: {
                    authType: 'subscriptionKey',
                    subscriptionKey: this.azureKey
                },
                style: 'grayscale_light',
                center: [-98, 39],
                zoom: 4,
                minZoom: 2,
                renderWorldCopies: false,
                language: 'en-US',
                view: 'Auto',
                showLogo: false,
                showFeedbackLink: false
            });

            this.map.events.add('ready', () => this.onMapReady());
            this.map.events.add('error', (e) => {
                this.showError('Map error occurred');
                this.setLoading(false);
            });
        } catch (error) {
            this.showError('Failed to create map');
            this.setLoading(false);
        }
    }

    onMapReady() {
        try {
            // eslint-disable-next-line
            this.ds = new atlas.source.DataSource();
            this.map.sources.add(this.ds);
            this.createCustomIcons();

            this.addLayerForStatus('critical', 'critical-icon');
            this.addLayerForStatus('high', 'high-icon');
            this.addLayerForStatus('medium', 'medium-icon');
            this.addLayerForStatus('low', 'low-icon');

            this.mapReady = true;
            this.setLoading(false);
            this.dispatchEvent(new CustomEvent('mapReady', {
                detail: { success: true },
                bubbles: true,
                composed: true
            }));

            if (this.sites.length) this.renderSites();
        } catch (error) {
            this.showError('Failed to initialize map');
            this.setLoading(false);
        }
    }

    createCustomIcons() {
        this.map.imageSprite.add('critical-icon', '/src/components/dataVisualization/map/assets/marker-critical.svg');
        this.map.imageSprite.add('high-icon', '/src/components/dataVisualization/map/assets/marker-high.svg');
        this.map.imageSprite.add('medium-icon', '/src/components/dataVisualization/map/assets/marker-medium.svg');
        this.map.imageSprite.add('low-icon', '/src/components/dataVisualization/map/assets/marker-low.svg');
    }

    addLayerForStatus(status, iconName) {
        // eslint-disable-next-line
        const layer = new atlas.layer.SymbolLayer(this.ds, null, {
            filter: ['==', ['get', 'status'], status],
            iconOptions: {
                image: iconName,
                allowOverlap: true,
                ignorePlacement: true,
                anchor: 'center'
            }
        });

        this.map.layers.add(layer);

        this.map.events.add('click', layer, (e) => {
            if (e.shapes?.length && e.pixel) {
                this.showStoreInfo(e.shapes[0].getProperties(), e.pixel);
            }
        });

        this.map.events.add('mouseenter', layer, () => {
            this.map.getCanvasContainer().style.cursor = 'pointer';
        });

        this.map.events.add('mouseleave', layer, () => {
            this.map.getCanvasContainer().style.cursor = 'grab';
        });
    }

    showStoreInfo(properties, pixel) {
        if (!properties || !properties.siteName) {
            return;
        }

        this.shadowRoot.getElementById('site-name').textContent = properties.siteName;
        this.shadowRoot.getElementById('site-address').textContent = properties.address || 'N/A';

        this.currentSiteId = properties.id;
        this.positionInfoPanel(pixel);

        this.infoPanel.style.display = 'block';
        this.infoPanel.offsetHeight;
        this.infoPanel.classList.add('visible');

        this.justOpened = true;
        setTimeout(() => {
            this.justOpened = false;
        }, 100);

        this.dispatchEvent(new CustomEvent('site-selected', {
            detail: { site: properties },
            bubbles: true,
            composed: true
        }));
    }

    positionInfoPanel(pixel) {
        const [clickX, clickY] = pixel;
        const mapRect = this.shadowRoot.getElementById('map').getBoundingClientRect();

        this.infoPanel.style.visibility = 'hidden';
        this.infoPanel.style.display = 'block';
        const panelWidth = this.infoPanel.offsetWidth || 260;
        const panelHeight = this.infoPanel.offsetHeight || 300;
        this.infoPanel.style.visibility = '';
        this.infoPanel.style.display = '';

        const isMobile = window.innerWidth < 768;
        const offset = isMobile ? 10 : 20;
        const padding = isMobile ? 8 : 16;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const maxWidth = isMobile ? Math.min(viewportWidth - padding * 2, mapRect.width - padding * 2) : mapRect.width - padding * 2;
        const maxHeight = isMobile ? Math.min(viewportHeight - padding * 2, mapRect.height - padding * 2) : mapRect.height - padding * 2;

        let left, top;

        if (isMobile && panelWidth > maxWidth) {
            left = padding;
            this.infoPanel.style.maxWidth = `${maxWidth}px`;
        } else if (clickX + offset + panelWidth < mapRect.width - padding) {
            left = clickX + offset;
        } else if (clickX - offset - panelWidth > padding) {
            left = clickX - offset - panelWidth;
        } else {
            left = Math.max(padding, (mapRect.width - panelWidth) / 2);
        }

        if (clickY + offset + panelHeight < mapRect.height - padding) {
            top = clickY + offset;
        } else if (clickY - offset - panelHeight > padding) {
            top = clickY - offset - panelHeight;
        } else {
            top = padding;
            this.infoPanel.style.maxHeight = `${maxHeight}px`;
            this.infoPanel.style.overflowY = 'auto';
        }

        if (isMobile) {
            if (left + panelWidth > viewportWidth - padding) {
                left = Math.max(padding, viewportWidth - panelWidth - padding);
            }
            if (top + panelHeight > viewportHeight - padding) {
                top = Math.max(padding, viewportHeight - panelHeight - padding);
                this.infoPanel.style.maxHeight = `${viewportHeight - top - padding}px`;
                this.infoPanel.style.overflowY = 'auto';
            }
        }

        left = Math.max(padding, Math.min(left, mapRect.width - Math.min(panelWidth, maxWidth) - padding));
        top = Math.max(padding, Math.min(top, mapRect.height - Math.min(panelHeight, maxHeight) - padding));

        this.infoPanel.style.left = `${left}px`;
        this.infoPanel.style.top = `${top}px`;
    }

    hideInfoPanel() {
        this.infoPanel.classList.remove('visible');
        this.currentSiteId = null;

        setTimeout(() => {
            if (!this.infoPanel.classList.contains('visible')) {
                this.infoPanel.style.display = 'none';
            }
        }, 200);
    }

    showError(message) {
        if (!message) {
            this.errorOverlay.classList.remove('visible');
            setTimeout(() => {
                if (!this.errorOverlay.classList.contains('visible')) {
                    this.errorOverlay.style.display = 'none';
                }
            }, 200);
            return;
        }
        this.errorOverlay.textContent = message;
        this.errorOverlay.style.display = 'block';
        this.errorOverlay.offsetHeight;
        this.errorOverlay.classList.add('visible');

        setTimeout(() => {
            this.errorOverlay.classList.remove('visible');
            setTimeout(() => {
                if (!this.errorOverlay.classList.contains('visible')) {
                    this.errorOverlay.style.display = 'none';
                }
            }, 200);
        }, 5000);
    }

    setLoading(isLoading) {
        if (isLoading) {
            this.loadingOverlay.style.display = 'flex';
            this.loadingOverlay.offsetHeight;
            this.loadingOverlay.classList.add('visible');
        } else {
            this.loadingOverlay.classList.remove('visible');
            setTimeout(() => {
                if (!this.loadingOverlay.classList.contains('visible')) {
                    this.loadingOverlay.style.display = 'none';
                }
            }, 200);
        }
    }

    renderSites() {
        if (!this.mapReady || !this.sites.length) return;

        this.setLoading(true);

        const features = [];

        for (const site of this.sites) {
        const lat = site.latitude || site.lat;
        const lon = site.longitude || site.lon;

        if (!isFinite(lat) || !isFinite(lon)) {
            continue;
        }

        features.push(this.createFeature(site, lat, lon));
    }

        if (!features.length) {
            this.showError('No valid site locations');
            this.setLoading(false);
            return;
        }

        try {
            this.ds.setShapes({ type: 'FeatureCollection', features });

            if (!this.hasFitBounds) {
                this.fitMapToPoints(features);
                this.hasFitBounds = true;
            }

            this.dispatchEvent(new CustomEvent('sites-rendered', {
                detail: { count: features.length },
                bubbles: true,
                composed: true
            }));
        } catch (error) {
            this.showError('Failed to render sites');
        }

        this.setLoading(false);
    }

    createFeature(site, lat, lon) {
        let status = 'low';
        if (site.status?.riskLevel === 3 || site.status === 'critical') {
            status = 'critical';
        } else if (site.status?.riskLevel === 2 || site.status === 'high') {
            status = 'high';
        } else if (site.status === 'medium') {
            status = 'medium';
        }

        const addressParts = [];
        if (site.street) addressParts.push(site.street);
        if (site.cityState) {
            addressParts.push(site.cityState);
        } else {
            const cityState = [site.city, site.stateProvince].filter(Boolean).join(', ');
            if (cityState) addressParts.push(cityState);
        }
        if (site.postalCode) addressParts.push(site.postalCode);

        return {
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [Number(lon), Number(lat)]
            },
            properties: {
                id: site.storeCpId || site.mdmSiteId || site.siteName || '',
                siteName: site.siteName || '',
                address: addressParts.join('\n') || 'N/A',
                status: status
            }
        };
    }

    fitMapToPoints(features) {
        if (!features.length) return;

        try {
            // eslint-disable-next-line
            const bounds = atlas.data.BoundingBox.fromData(features);
            if (bounds && bounds.every(v => isFinite(v))) {
                this.map.setCamera({
                    bounds,
                    padding: { top: 80, bottom: 80, left: 80, right: 80 },
                    duration: 800,
                    type: 'ease'
                });
            }
        } catch (e) {
            console.warn('[NeptuneMap] bounding box error', e);
        }
    }

    computeSignature(sites) {
        return sites
            .map(s => {
                const id = s.storeCpId || s.mdmSiteId || s.siteName || '';
                const lat = s.latitude || s.lat || '';
                const lon = s.longitude || s.lon || '';
                return `${id}:${lat}:${lon}`;
            })
            .sort()
            .join('|');
    }

    setData(sites = []) {
    if (!Array.isArray(sites)) {
        console.warn('[NeptuneMap] setData expects an array');
        return;
    }

    const signature = this.computeSignature(sites);
    if (signature === this.lastSignature) return;

    this.lastSignature = signature;
    this.sites = sites;
    this.hasFitBounds = false;

    if (this.mapReady) this.renderSites();
}

    clearSites() {
        this.sites = [];
        this.lastSignature = '';
        this.hasFitBounds = false;
        if (this.ds) this.ds.clear();
        this.hideInfoPanel();
    }

    zoomToSite(siteId) {
        const site = this.sites.find(s =>
            s.storeCpId === siteId ||
            s.mdmSiteId === siteId ||
            s.siteName === siteId
        );

        if (site) {
            const lat = site.latitude || site.lat;
            const lon = site.longitude || site.lon;

            if (isFinite(lat) && isFinite(lon)) {
                this.map.setCamera({
                    center: [lon, lat],
                    zoom: 12,
                    duration: 1000,
                    type: 'ease'
                });
            }
        }
    }

    resetView() {
        if (this.sites.length && this.ds) {
            const shapes = this.ds.getShapes();
            if (shapes.length) this.fitMapToPoints(shapes);
        } else {
            this.map.setCamera({ center: [-98, 39], zoom: 4, duration: 800, type: 'ease' });
        }
        this.hideInfoPanel();
    }
}

if (!customElements.get('neptune-map')) {
    customElements.define('neptune-map', NeptuneMap);
}
export default NeptuneMap;